import { useState } from 'react';
import { faNum as fa, formatRial, type LandedBreakdownDto } from '@kia/shared';
import { api } from '../api/client';
import { postPrice } from '../api/priceWrite';
import { Button, Card, EntityPicker, Field, NumberInput, useConfirm, useToast } from '../ui';

interface MaterialPick {
  code: string;
  name: string;
}

/**
 * محاسبهٔ قیمت تمام‌شدهٔ تحویل‌درِ‌کارخانه (landed) — the costing office enters a material's import
 * components and gets the delivered ریال/kg (or ریال/piece) to use as its price. A pure calculator
 * (POST /api/motor/landed-cost); the result is entered as the material's price so the motor COGS is on a
 * true landed basis. Domestic material ⇒ leave the import percentages at zero.
 */

export function LandedCostPanel() {
  const toast = useToast();
  const confirm = useConfirm();
  const [base, setBase] = useState(100_000);
  const [freight, setFreight] = useState(0.1);
  const [duty, setDuty] = useState(0.05);
  const [levy, setLevy] = useState(0.09);
  const [clearance, setClearance] = useState(5_000);
  const [financing, setFinancing] = useState(0.03);
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState<LandedBreakdownDto | null>(null);
  // TKT-10b: which material this landed cost is for, so the result can be written straight to its price
  // instead of hand-copied.
  const [mat, setMat] = useState<MaterialPick | null>(null);
  const [saving, setSaving] = useState(false);

  async function saveAsPrice() {
    if (!mat || !out) return;
    setSaving(true);
    const r = await postPrice('/api/prices/mat', { code: mat.code, price: out.landed }, confirm);
    setSaving(false);
    if (!r.ok) return toast((r.body as { message?: string }).message ?? 'خطا در ثبت قیمت', 'error');
    toast(`بهای «${mat.name}» روی ${formatRial(out.landed)} ﷼ تنظیم شد`);
  }

  async function compute() {
    if (!(base >= 0)) {
      toast('قیمت پایه نامعتبر است', 'error');
      return;
    }
    setBusy(true);
    const r = await api<LandedBreakdownDto>('/api/motor/landed-cost', {
      method: 'POST',
      body: JSON.stringify({
        basePerUnit: base,
        freightInsurancePct: freight,
        customsDutyPct: duty,
        importLevyPct: levy,
        clearanceHandlingPerUnit: clearance,
        financingPct: financing,
      }),
    });
    setBusy(false);
    if (!r.ok) {
      toast((r.body as { message?: string }).message ?? 'خطا در محاسبه', 'error');
      return;
    }
    setOut(r.body as LandedBreakdownDto);
  }

  return (
    <Card title="محاسبهٔ قیمت تمام‌شدهٔ تحویل (Landed) — برای واحد بهای تمام‌شده">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))',
          gap: 12,
        }}
      >
        <Field label="قیمت پایه (ریال/واحد)">
          <NumberInput value={base} onValue={setBase} />
        </Field>
        <Field label="کرایه و بیمه (٪)">
          <NumberInput value={freight} onValue={setFreight} allowDecimal />
        </Field>
        <Field label="حقوق ورودی گمرک (٪)">
          <NumberInput value={duty} onValue={setDuty} allowDecimal />
        </Field>
        <Field label="مالیات/سود بازرگانی (٪)">
          <NumberInput value={levy} onValue={setLevy} allowDecimal />
        </Field>
        <Field label="ترخیص و حمل داخلی (ریال/واحد)">
          <NumberInput value={clearance} onValue={setClearance} />
        </Field>
        <Field label="تأمین مالی/نسیه (٪)">
          <NumberInput value={financing} onValue={setFinancing} allowDecimal />
        </Field>
      </div>
      <div style={{ marginTop: 12 }}>
        <Button onClick={() => void compute()} disabled={busy}>
          {busy ? 'در حال محاسبه…' : 'محاسبهٔ قیمت تحویل'}
        </Button>
      </div>

      <div style={{ marginTop: 12 }}>
        <EntityPicker<MaterialPick>
          label="مادهٔ متناظر (برای ثبت مستقیم قیمت)"
          endpoint="/api/materials"
          maxResults={6}
          itemKey={(m) => m.code}
          placeholder="جستجوی ماده…"
          renderResult={(m) => ({ label: m.name, subtitle: `(${m.code})` })}
          onSelect={(m) => setMat({ code: m.code, name: m.name })}
        />
        {mat ? (
          <p className="muted" style={{ marginTop: 6 }}>
            مادهٔ انتخاب‌شده: {mat.name} <span className="num">({mat.code})</span>
          </p>
        ) : null}
      </div>

      {out ? (
        <table className="table" style={{ marginTop: 12 }}>
          <tbody>
            <tr>
              <td>قیمت پایه</td>
              <td>{formatRial(out.base)}</td>
            </tr>
            <tr>
              <td>CIF (پایه + کرایه و بیمه)</td>
              <td>{formatRial(out.cif)}</td>
            </tr>
            <tr>
              <td>حقوق ورودی</td>
              <td>{formatRial(out.duty)}</td>
            </tr>
            <tr>
              <td>مالیات/سود بازرگانی</td>
              <td>{formatRial(out.levy)}</td>
            </tr>
            <tr>
              <td>ترخیص و حمل</td>
              <td>{formatRial(out.clearance)}</td>
            </tr>
            <tr>
              <td>تأمین مالی</td>
              <td>{formatRial(out.financing)}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>
                <b>قیمت تمام‌شدهٔ تحویل (landed)</b>
              </td>
              <td>
                <b>{formatRial(out.landed)}</b>
              </td>
            </tr>
          </tfoot>
        </table>
      ) : null}
      {out ? (
        <div style={{ marginTop: 10 }}>
          <Button onClick={() => void saveAsPrice()} disabled={!mat || saving}>
            {saving
              ? 'در حال ثبت…'
              : mat
                ? `ثبت به‌عنوان بهای «${mat.name}»`
                : 'ابتدا مادهٔ متناظر را انتخاب کنید'}
          </Button>
        </div>
      ) : null}
      <p style={{ marginTop: 8, color: 'var(--ink-3)' }}>
        این قیمت بهای تمام‌شدهٔ موتور را بر مبنای واقعیِ تحویل قرار می‌دهد؛ با دکمهٔ بالا مستقیماً
        روی بهای ماده ثبت می‌شود.
        {out ? ` (${fa((out.landed / out.base - 1) * 100, 0)}٪ بالاتر از قیمت پایه)` : ''}
      </p>
    </Card>
  );
}
